import React, { useState, useCallback } from 'react';
import {
  Facebook, Linkedin, Mail, MessageCircle, Instagram, Twitter, Link2, Check,
} from 'lucide-react';

/**
 * SocialShareButtons — a row of share targets for a public URL.
 *
 * Targets (in order):
 *   - WhatsApp       → wa.me intent (mobile opens app, desktop opens web)
 *   - Facebook       → sharer/sharer.php
 *   - X (Twitter)    → twitter.com/intent/tweet
 *   - LinkedIn       → linkedin.com/sharing/share-offsite
 *   - Email          → mailto: with prefilled subject + body
 *   - Instagram      → "Copy Link for Instagram" (no public web share intent)
 *   - Copy Link      → navigator.clipboard.writeText
 *
 * All web-share targets open in a new tab with rel=noopener,noreferrer.
 * The URL is encodeURIComponent'd before insertion into the intent URL.
 *
 * Props:
 *   url           — string, required. The URL to share.
 *   title         — string, optional. Used as tweet text / email subject
 *                   / WhatsApp message prefix.
 *   description   — string, optional. Used as email body and the longer
 *                   text on WhatsApp.
 *   onCopy        — optional callback(label) fired after a successful
 *                   copy-to-clipboard. Use for surfacing a toast from
 *                   the parent. label is one of "link" | "instagram".
 *   compact       — when true, hides text labels and shows icon-only
 *                   buttons. Useful for cramped modal footers.
 */
export default function SocialShareButtons({
  url,
  title = '',
  description = '',
  onCopy,
  compact = false,
}) {
  const [copiedLabel, setCopiedLabel] = useState(null);

  const encodedUrl = encodeURIComponent(url || '');
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description || title);

  const openInNewTab = useCallback((href) => {
    if (typeof window === 'undefined') return;
    window.open(href, '_blank', 'noopener,noreferrer');
  }, []);

  const copyText = useCallback(async (text, label) => {
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedLabel(label);
      if (onCopy) onCopy(label);
      setTimeout(() => setCopiedLabel((cur) => (cur === label ? null : cur)), 2000);
    } catch { /* ignore */ }
  }, [onCopy]);

  // Native share intent URLs. Each platform has documented intent
  // schemes that work on both desktop browsers and mobile apps.
  const targets = [
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      Icon: MessageCircle,
      iconClass: 'text-green-600',
      onClick: () => openInNewTab(
        `https://wa.me/?text=${encodedTitle ? encodedTitle + '%20' : ''}${encodedUrl}`
      ),
    },
    {
      key: 'facebook',
      label: 'Facebook',
      Icon: Facebook,
      iconClass: 'text-[#1877F2]',
      onClick: () => openInNewTab(
        `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
      ),
    },
    {
      key: 'x',
      label: 'X',
      Icon: Twitter,
      iconClass: 'text-gray-900',
      onClick: () => openInNewTab(
        `https://twitter.com/intent/tweet?url=${encodedUrl}${encodedTitle ? '&text=' + encodedTitle : ''}`
      ),
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      Icon: Linkedin,
      iconClass: 'text-[#0A66C2]',
      onClick: () => openInNewTab(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`
      ),
    },
    {
      key: 'email',
      label: 'Email',
      Icon: Mail,
      iconClass: 'text-gray-700',
      onClick: () => {
        const subject = encodedTitle || encodeURIComponent('Check this out');
        const body = `${encodedDescription ? encodedDescription + '%0A%0A' : ''}${encodedUrl}`;
        // Mailto stays on the same surface — different from web intents.
        if (typeof window !== 'undefined') {
          window.location.href = `mailto:?subject=${subject}&body=${body}`;
        }
      },
    },
    {
      key: 'instagram',
      label: 'Copy for Instagram',
      Icon: Instagram,
      iconClass: 'text-pink-600',
      onClick: () => copyText(url, 'instagram'),
      title: 'Instagram does not support direct web sharing — paste this in your post or DM',
    },
    {
      key: 'copy',
      label: copiedLabel === 'link' ? 'Copied!' : 'Copy Link',
      Icon: copiedLabel === 'link' ? Check : Link2,
      iconClass: copiedLabel === 'link' ? 'text-green-600' : 'text-gray-700',
      onClick: () => copyText(url, 'link'),
    },
  ];

  if (!url) return null;

  return (
    <div
      data-testid="social-share-buttons"
      className={`flex flex-wrap gap-2 ${compact ? 'justify-center' : ''}`}
    >
      {targets.map(({ key, label, Icon, iconClass, onClick, title: tip }) => {
        const isCopiedRow = (key === 'copy' && copiedLabel === 'link') ||
                            (key === 'instagram' && copiedLabel === 'instagram');
        return (
          <button
            key={key}
            type="button"
            onClick={onClick}
            title={tip || label}
            aria-label={label}
            data-testid={`share-target-${key}`}
            className={`flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-2 text-xs font-medium hover:bg-gray-50 transition-colors ${compact ? 'px-2' : ''} ${isCopiedRow ? 'border-green-300 bg-green-50' : ''}`}
          >
            <Icon className={`w-4 h-4 ${iconClass}`} />
            {!compact && (
              <span className="hidden sm:inline">{isCopiedRow && key === 'instagram' ? 'Copied!' : label}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
